/**
 * Grab a still from near the end of a clip, to use as its poster.
 *
 * The first frame is the guest's own face — recording starts at the trigger so
 * the transformation is in the clip — which makes a poor cover image. A frame
 * shortly before the end is the guest fully in costume.
 *
 * Scope: this sets the poster for our own <video>. It does not change the
 * thumbnail iOS shows in the share sheet or Photos, which is derived from the
 * encoded file's first frame and is not ours to set.
 */

const DEFAULT_OFFSET_SEC = 0.5;
/** Seeking and decoding a frame should be near-instant; never hang the screen. */
const TIMEOUT_MS = 4000;

/**
 * The recording canvas's last frame, read the moment the recorder stops.
 *
 * Same picture `posterFromClip` goes looking for — the guest in costume at the
 * end of the take — but without opening a second decoder for a file we have
 * only just finished writing. That decode is why the result screen opened on a
 * full-screen grey rectangle for about a second on the unit: a <video> with no
 * poster paints the WebView's own placeholder until its first frame lands, and
 * on this board a fresh VP8 decode arrives long after the screen does.
 *
 * Cheap enough to be unconditional: one JPEG encode of a 720x1280 canvas, at
 * the moment the screen is changing anyway. Returns null rather than throwing —
 * a tainted canvas (it should not be; every source is same-origin or a
 * MediaStream) leaves `posterFromClip` to do it the slow way.
 */
export function posterFromCanvas(canvas: HTMLCanvasElement): string | null {
  if (!canvas.width || !canvas.height) return null;
  try {
    return canvas.toDataURL('image/jpeg', 0.85);
  } catch {
    return null;
  }
}

function once<T extends Event>(
  target: EventTarget,
  event: string,
  timeoutMs: number,
): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      target.removeEventListener(event, handler as EventListener);
      resolve(null);
    }, timeoutMs);
    const handler = (e: Event) => {
      window.clearTimeout(timer);
      target.removeEventListener(event, handler as EventListener);
      resolve(e as T);
    };
    target.addEventListener(event, handler as EventListener, { once: true });
  });
}

/**
 * @param fallbackDurationMs the recorder's own measured duration, used when the
 *   file does not report one. MediaRecorder output routinely has a duration of
 *   Infinity because the container is written without a length up front.
 */
export async function posterFromClip(
  blob: Blob,
  fallbackDurationMs: number,
  offsetSec = DEFAULT_OFFSET_SEC,
): Promise<string | null> {
  const url = URL.createObjectURL(blob);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.src = url;

  try {
    const meta = await once(video, 'loadedmetadata', TIMEOUT_MS);
    if (!meta) return null;

    const reported = video.duration;
    const durationSec = Number.isFinite(reported) && reported > 0
      ? reported
      : fallbackDurationMs / 1000;
    if (!Number.isFinite(durationSec) || durationSec <= 0) return null;

    // Clamp so a clip shorter than the offset still yields a frame.
    video.currentTime = Math.max(0, durationSec - offsetSec);
    const seeked = await once(video, 'seeked', TIMEOUT_MS);
    if (!seeked) return null;

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, width, height);

    return canvas.toDataURL('image/jpeg', 0.85);
  } catch {
    return null;
  } finally {
    // Detach before revoking or Safari can keep decoding a dead URL.
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}
